import React, { useEffect, useState } from 'react';
import { Archive, CheckCircle, AlertCircle, Info, Plus, Trash2, X } from 'lucide-react';
import { getZipBundles, saveZipBundles } from '../../utils/api';

const DEFAULT_BUNDLE = {
  name: '',
  enabled: true,
  zipNameKeywords: [],
  zipNameMatchMode: 'contains',
  allowedSenders: [],
  maxUncompressedBytes: 524288000,
  maxMemberCount: 200,
  allowNested: false,
  notes: ''
};

const ChipInput = ({ chips, onAdd, onRemove, placeholder, normalize }) => {
  const [val, setVal] = useState('');
  const commit = () => {
    const v = normalize ? normalize(val) : val.trim();
    if (v && !chips.includes(v)) onAdd(v);
    setVal('');
  };
  return (
    <div className="flex flex-wrap gap-1.5 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 min-h-[42px]">
      {chips.map((c) => (
        <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 text-sm font-medium">
          {c}
          <button type="button" onClick={() => onRemove(c)} className="text-blue-500 hover:text-blue-700 leading-none">
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          else if (e.key === 'Backspace' && !val && chips.length > 0) onRemove(chips[chips.length - 1]);
        }}
        onBlur={commit}
        placeholder={chips.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[120px] outline-none bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400"
      />
    </div>
  );
};

const bytesToMb = (b) => Math.round((b / (1024 * 1024)) * 10) / 10;
const mbToBytes = (mb) => Math.max(1, Math.round(Number(mb) * 1024 * 1024));

const validate = (bundles) => {
  const errors = [];
  const seen = new Set();
  bundles.forEach((b, i) => {
    if (!b.name || !b.name.trim()) errors.push(`Bundle #${i + 1}: name is required`);
    else if (seen.has(b.name.trim().toLowerCase())) errors.push(`Bundle #${i + 1}: name "${b.name}" is duplicated`);
    else seen.add(b.name.trim().toLowerCase());

    if (!Array.isArray(b.zipNameKeywords) || b.zipNameKeywords.length === 0) {
      errors.push(`Bundle #${i + 1}: at least one zip-name keyword is required`);
    } else if (b.zipNameKeywords.some(k => k.trim().length < 2)) {
      errors.push(`Bundle #${i + 1}: each keyword must be at least 2 characters`);
    }
    if (!(b.maxUncompressedBytes > 0)) errors.push(`Bundle #${i + 1}: max uncompressed size must be positive`);
    if (!(b.maxMemberCount > 0)) errors.push(`Bundle #${i + 1}: max member count must be positive`);
  });
  return errors;
};

const ZipBundleConfig = ({ hotelId, onClose }) => {
  const [bundles, setBundles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  useEffect(() => {
    if (!hotelId) return;
    setIsLoading(true);
    getZipBundles(hotelId)
      .then(res => setBundles(Array.isArray(res?.zipBundles) ? res.zipBundles : []))
      .catch(err => setError(err?.response?.data?.error || err.message || 'Failed to load zip bundles'))
      .finally(() => setIsLoading(false));
  }, [hotelId]);

  const updateBundle = (idx, patch) => {
    setBundles(prev => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
    setSuccessMsg(null);
  };

  const addBundle = () => {
    setBundles(prev => [...prev, { ...DEFAULT_BUNDLE }]);
    setSuccessMsg(null);
  };

  const removeBundle = (idx) => {
    setBundles(prev => prev.filter((_, i) => i !== idx));
    setSuccessMsg(null);
  };

  const handleSave = async () => {
    const errs = validate(bundles);
    if (errs.length > 0) {
      setError(errs.join(' • '));
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      const res = await saveZipBundles(hotelId, bundles);
      setBundles(Array.isArray(res?.zipBundles) ? res.zipBundles : bundles);
      setSuccessMsg('Zip bundle configuration saved.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to save zip bundles');
    } finally {
      setIsSaving(false);
    }
  };

  if (!hotelId) {
    return (
      <div className="p-4 text-sm text-gray-600 dark:text-gray-400">
        Select a hotel before configuring zip bundles.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Archive className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Approved Zip Bundles</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
        <div className="flex items-start space-x-2 text-sm text-blue-700 dark:text-blue-400">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p>The ETL <strong>only unpacks a <code>.zip</code> attachment</strong> if its filename matches one of the bundles below. Unlisted zips are logged and dropped without being opened.</p>
            <p>After extraction, each member file is re-run through your existing file-type matchers (audit, occupancy, revenue, etc.), so the same <code>fileConfigs</code> control which members actually get processed.</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : (
        <>
          {bundles.length === 0 && (
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No zip bundles configured. Click <strong>Add bundle</strong> to approve a zip pattern for this hotel.
            </div>
          )}

          {bundles.map((b, idx) => (
            <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-md p-4 space-y-4 bg-white dark:bg-gray-800">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Bundle name</label>
                  <input
                    type="text"
                    value={b.name}
                    onChange={(e) => updateBundle(idx, { name: e.target.value })}
                    placeholder="e.g., Daily Reports Bundle"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
                <div className="pt-6 flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={b.enabled !== false}
                      onChange={(e) => updateBundle(idx, { enabled: e.target.checked })}
                    />
                    Enabled
                  </label>
                  <button
                    onClick={() => removeBundle(idx)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    aria-label="Remove bundle"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div>
                <label className="cp-label">
                  Zip filename keywords <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  The zip is approved when its filename contains any of these (case-insensitive). Press Enter to add.
                </p>
                <ChipInput
                  chips={b.zipNameKeywords}
                  onAdd={(kw) => updateBundle(idx, { zipNameKeywords: [...b.zipNameKeywords, kw] })}
                  onRemove={(kw) => updateBundle(idx, { zipNameKeywords: b.zipNameKeywords.filter(k => k !== kw) })}
                  placeholder="e.g., daily_reports, eod_bundle"
                />
              </div>

              <div>
                <label className="cp-label">Match mode</label>
                <div className="flex gap-2">
                  {['contains', 'exact'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => updateBundle(idx, { zipNameMatchMode: mode })}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        b.zipNameMatchMode === mode
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="cp-label">
                  Allowed senders <span className="text-gray-400">(optional)</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  If set, the zip is only extracted when the From address matches (substring, case-insensitive). Leave empty to skip this check.
                </p>
                <ChipInput
                  chips={b.allowedSenders || []}
                  onAdd={(s) => updateBundle(idx, { allowedSenders: [...(b.allowedSenders || []), s.toLowerCase()] })}
                  onRemove={(s) => updateBundle(idx, { allowedSenders: (b.allowedSenders || []).filter(x => x !== s) })}
                  placeholder="e.g., reports@vendor.com"
                  normalize={(v) => v.trim().toLowerCase()}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="cp-label">Max uncompressed size (MB)</label>
                  <input
                    type="number"
                    min="1"
                    value={bytesToMb(b.maxUncompressedBytes)}
                    onChange={(e) => updateBundle(idx, { maxUncompressedBytes: mbToBytes(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="cp-label">Max member count</label>
                  <input
                    type="number"
                    min="1"
                    value={b.maxMemberCount}
                    onChange={(e) => updateBundle(idx, { maxMemberCount: Math.max(1, parseInt(e.target.value || '0', 10)) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
                <div className="pt-6">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={b.allowNested === true}
                      onChange={(e) => updateBundle(idx, { allowNested: e.target.checked })}
                    />
                    Allow nested archives
                  </label>
                </div>
              </div>

              <div>
                <label className="cp-label">Notes (optional)</label>
                <textarea
                  value={b.notes || ''}
                  onChange={(e) => updateBundle(idx, { notes: e.target.value })}
                  rows={2}
                  placeholder="e.g., Vendor X sends these nightly at 06:00 UTC"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
            </div>
          ))}

          <button
            onClick={addBundle}
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Plus size={16} /> Add bundle
          </button>
        </>
      )}

      {error && (
        <div className="p-3 rounded-md border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="p-3 rounded-md border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-400 flex items-start gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{successMsg}</span>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onClose && (
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Close
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {isSaving ? 'Saving…' : 'Save zip bundles'}
        </button>
      </div>
    </div>
  );
};

export default ZipBundleConfig;
